import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, from, of } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { environment } from '@assets/environment/environment';

// Declare global branch interface for TypeScript
declare global {
  interface Window {
    branch: any;
  }
}

// AugmentCode Summary:
// Branch.io service for handling deep linking, link creation, and coupon code extraction

export interface BranchLinkData {
  requirePaymentMethod?: boolean;
  couponCode?: string;
  campaign?: string;
  channel?: string;
  feature?: string;
  stage?: string;
  tags?: string[];
  data?: any;
}

export interface BranchLinkOptions {
  alias?: string;
  type?: number;
  data?: BranchLinkData;
}

interface CachedLinkData {
  data: BranchLinkData;
  timestamp: number;
}

@Injectable({
  providedIn: 'root',
})
export class BranchService {
  private branchInitialized = new BehaviorSubject<boolean>(false);
  private linkData = new BehaviorSubject<BranchLinkData | null>(null);
  private isInitializing = false;
  private readonly CACHE_KEY = 'branch_link_data';
  private readonly CACHE_TTL = 24 * 60 * 60 * 1000; // 24 hours in milliseconds

  constructor() {
    // this.restoreCachedData();
  }

  /**
   * Restore cached link data from localStorage if available and not expired
   */
  private restoreCachedData(): void {
    try {
      const cached = localStorage.getItem(this.CACHE_KEY);
      if (cached) {
        const cachedItem: CachedLinkData = JSON.parse(cached);
        const now = new Date().getTime();

        // Check if cache is still valid
        if (now - cachedItem.timestamp < this.CACHE_TTL) {
          console.log('Restoring cached Branch link data:', cachedItem.data);
          this.linkData.next(cachedItem.data);
        } else {
          // Cache expired, remove it
          this.clearCache();
        }
      }
    } catch (error) {
      console.error('Error restoring cached Branch data:', error);
      this.clearCache();
    }
  }

  /**
   * Cache link data to localStorage
   */
  private cacheData(data: BranchLinkData): void {
    try {
      const cacheItem: CachedLinkData = {
        data,
        timestamp: new Date().getTime(),
      };
      localStorage.setItem(this.CACHE_KEY, JSON.stringify(cacheItem));
      console.log('Branch link data cached');
    } catch (error) {
      console.error('Error caching Branch data:', error);
    }
  }

  /**
   * Clear cached link data from localStorage
   */
  clearCache(): void {
    try {
      localStorage.removeItem(this.CACHE_KEY);
      console.log('Branch link data cache cleared');
    } catch (error) {
      console.error('Error clearing Branch cache:', error);
    }
  }

  /**
   * Initialize Branch.io SDK
   * @returns Promise that resolves when Branch is initialized
   */
  async initialize(): Promise<void> {
    if (this.branchInitialized.value || this.isInitializing) {
      return;
    }

    this.isInitializing = true;

    try {
      // Initialize Branch with your key
      await new Promise<void>((resolve, reject) => {
        window.branch.init(environment.branchKey, {}, (err: any, data: any) => {
          if (err) {
            console.error('Branch initialization error:', err);
            reject(err);
          } else {
            console.log('Branch initialized successfully:', data);

            // Store initial link data if available
            if (data && Object.keys(data?.data_parsed).length > 0) {
              console.log('Initial link data:', data?.data_parsed);
              this.linkData.next(data?.data_parsed);
              // Cache the data for persistence across page refreshes
              this.cacheData(data?.data_parsed);
            } else {
              this.restoreCachedData();
            }

            this.branchInitialized.next(true);
            resolve();
          }
        });
      });
    } catch (error) {
      console.error('Failed to initialize Branch:', error);
      throw error;
    } finally {
      this.isInitializing = false;
    }
  }

  /**
   * Check if Branch is initialized
   */
  isInitialized(): Observable<boolean> {
    return this.branchInitialized.asObservable();
  }

  /**
   * Get current link data
   */
  getLinkData(): Observable<BranchLinkData | null> {
    return this.linkData.asObservable();
  }

  /**
   * Extract coupon code from current link data
   */
  getCouponCode(): string | null {
    const currentData = this.linkData.value;
    return currentData?.couponCode || currentData?.data?.couponCode || null;
  }

  /**
   * Extract requirePaymentMethod from current link data
   */
  getRequirePaymentMethod(): boolean {
    const currentData = this.linkData.value;
    return (
      currentData?.requirePaymentMethod ||
      currentData?.data?.requirePaymentMethod ||
      false
    );
  }

  /**
   * Track a custom event with Branch.io
   * @param eventName Name of the event
   * @param eventData Additional data for the event
   */
  trackEvent(eventName: string, eventData?: any): void {
    if (!this.branchInitialized.value) {
      console.warn('Branch not initialized, cannot track event');
      return;
    }

    window.branch.track(eventName, eventData, (err: any) => {
      if (err) {
        console.error('Error tracking Branch event:', err);
      } else {
        console.log(`Branch event tracked: ${eventName}`, eventData);
      }
    });
  }

  /**
   * Track commerce event (for purchases)
   * @param revenue Revenue amount
   * @param currency Currency code (default: USD)
   * @param transactionId Transaction identifier
   * @param couponCode Applied coupon code
   * @param additionalData Additional event data
   */
  trackPurchase(revenue: number, couponCode?: string): void {
    if (!this.branchInitialized.value) {
      console.warn('Branch not initialized, cannot track purchase');
      return;
    }

    const eventData = {
      revenue,
      coupon: couponCode,
    };

    window.branch.track('purchase', eventData, (err: any) => {
      if (err) {
        console.error('Error tracking Branch purchase:', err);
      } else {
        console.log('Branch purchase tracked:', eventData);
      }
    });
  }

  /**
   * Set user identity for Branch.io
   * @param userId User identifier
   */
  setIdentity(userId: string): Observable<void> {
    if (!this.branchInitialized.value) {
      return of(undefined).pipe(
        tap(() => console.warn('Branch not initialized, cannot set identity'))
      );
    }

    return from(
      new Promise<void>((resolve, reject) => {
        window.branch.setIdentity(userId, (err: any, data: any) => {
          if (err) {
            console.error('Error setting Branch identity:', err);
            reject(err);
          } else {
            console.log('Branch identity set:', data);
            resolve();
          }
        });
      })
    ).pipe(
      catchError((error) => {
        console.error('Failed to set Branch identity:', error);
        return of(undefined);
      })
    );
  }

  /**
   * Logout user from Branch.io
   */
  logout(): Observable<void> {
    if (!this.branchInitialized.value) {
      return of(undefined);
    }

    return from(
      new Promise<void>((resolve, reject) => {
        window.branch.logout((err: any) => {
          if (err) {
            console.error('Error logging out from Branch:', err);
            reject(err);
          } else {
            console.log('Branch logout successful');
            resolve();
          }
        });
      })
    ).pipe(
      catchError((error) => {
        console.error('Failed to logout from Branch:', error);
        return of(undefined);
      })
    );
  }

  /**
   * Get referral data
   */
  getReferralData(): Observable<any> {
    if (!this.branchInitialized.value) {
      return of(null);
    }

    return from(
      new Promise<any>((resolve, reject) => {
        window.branch.data((err: any, data: any) => {
          if (err) {
            console.error('Error getting Branch referral data:', err);
            reject(err);
          } else {
            resolve(data);
          }
        });
      })
    ).pipe(
      catchError((error) => {
        console.error('Failed to get Branch referral data:', error);
        return of(null);
      })
    );
  }
}
